import { type MouseEvent, useEffect, useState } from 'react';
import { useTooltip } from '@nivo/tooltip';
import { labelValuesStr } from '../../utils/formatCharts/handleCustomValue';

interface Metric {
  value: number;
  value2: number;
}
interface barPart {
  groupId: string
  stackedValue: number,
  needle: Metric,
}

interface styleConfigProps {
  StartAngleControl: number;
  EndAngleControl: number;
  EnableNeedleLabelControl: boolean;
  NeedleLabelColorControl: string;
  NeedleLabelFontFamilyControl: string;
  NeedleLabelFontSizeControl: number;
  NeedleLabelRotationControl: number;
  NeedleLabelTranslateX: number;
  NeedleLabelTranslateY: number;
  NeedleColorControl: string;
  NeedleHeightControl: number;
  NeedleWidthControl: number;
  NeedlePointWidthControl: number;
  DataFormat: { type: 'percentual' | 'absolute' | 'both' };
}

interface globalConfigProps {
  useGlobalFont: boolean;
  fontLabel: string;
}

interface NeedleProps {
  metric: Metric;
  bars: barPart[];
  valueScale: (value: number) => number;
  outerRadius: number;
  center: [number, number];
  tooltip: CallableFunction;
  styleConfig: styleConfigProps;
  globalConfig: globalConfigProps;
}

const Needle = ({
  metric, bars, valueScale, outerRadius, center, tooltip, styleConfig, globalConfig,
}: NeedleProps) => {
  const tip = useTooltip();
  const [needleAt, setNeedleAt] = useState<Partial<barPart>>({
    groupId: 'needle-tooltip',
    needle: metric,
  });

  const handleTooltip = (e: MouseEvent) => (
    tip.showTooltipFromEvent(tooltip({ bar: needleAt }), e)
  );

  useEffect(() => {
    setNeedleAt(metric.value > 0 ? ({
      ...bars.find((b) => metric.value <= b.stackedValue),
      needle: metric,
    }) : {
      groupId: 'needle-tooltip',
      needle: metric,
    });
  }, [metric, bars]);

  return (
    <g>
      <circle
        r={outerRadius * (styleConfig.NeedleWidthControl / 2)}
        cx={center[0]}
        cy={center[1]}
        onMouseEnter={(e: MouseEvent<SVGCircleElement>) => handleTooltip(e)}
        onMouseMove={(e: MouseEvent<SVGCircleElement>) => handleTooltip(e)}
        onMouseLeave={tip.hideTooltip}
        fillOpacity="1"
        fill={styleConfig.NeedleColorControl}
      />
      <polygon
        fillOpacity="1"
        fill={styleConfig.NeedleColorControl}
        points={`
          ${center[0] - outerRadius * (styleConfig.NeedleWidthControl / 3)},
          ${center[1]}
          ${center[0] - outerRadius * (styleConfig.NeedleWidthControl / 3) * styleConfig.NeedlePointWidthControl},
          ${center[1] + outerRadius * styleConfig.NeedleHeightControl}
          ${center[0] + outerRadius * (styleConfig.NeedleWidthControl / 3) * styleConfig.NeedlePointWidthControl},
          ${center[1] + outerRadius * styleConfig.NeedleHeightControl}
          ${center[0] + outerRadius * (styleConfig.NeedleWidthControl / 3)},
          ${center[1]}
        `}
        transform={`
          rotate(${180 + (+valueScale(metric.value))})
        `}
        style={{
          transformOrigin: `${center[0]}px ${center[1]}px`,
        }}
        onMouseEnter={(e: MouseEvent<SVGPolygonElement>) => handleTooltip(e)}
        onMouseMove={(e: MouseEvent<SVGPolygonElement>) => handleTooltip(e)}
        onMouseLeave={tip.hideTooltip}
      />
      {styleConfig.EnableNeedleLabelControl && (
        <text
          dx={center[0]}
          dy={center[1]}
          fillOpacity="1"
          fill={styleConfig.NeedleLabelColorControl}
          dominantBaseline="central"
          textAnchor="middle"
          style={{
            fontSize: `${styleConfig.NeedleLabelFontSizeControl}px`,
            fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontLabel : styleConfig.NeedleLabelFontFamilyControl}', Arial`,
            transform: `translate(${styleConfig.NeedleLabelTranslateX}px, ${styleConfig.NeedleLabelTranslateY}px) rotate(${styleConfig.NeedleLabelRotationControl}deg)`,
            transformOrigin: 'center center',
            transformBox: 'fill-box',
            outlineWidth: '0px',
            outlineColor: 'transparent',
            pointerEvents: 'none',
          }}
        >
          <tspan>
            {labelValuesStr(metric.value, metric.value2, styleConfig)}
          </tspan>
        </text>
      )}
    </g>
  );
};

export default Needle;
